import { FormMultiSelectField } from 'components/form/FormMultiSelectField';
import { FormPhoneNumberField } from 'components/form/FormPhoneNumberField';
import FormTextField from 'components/form/FormTextField';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { FormValues } from '../schema';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import { SelectOptionRadioButton } from 'components/radiobuttons/SelectOptionRadioButtons';
import { useVerificationContext } from '../helpers/verificationContext';
import {
    hasBankingProduct,
    hasCryptoOTCProduct,
    hasMerchantProduct,
    hasPortlProduct,
} from '../productUtils';
import Button from 'components/button/Button';
import BackButton from '../helpers/BackButton';
import { WrappedFormSingleSelectField, SelectOption } from 'components/form/FormSingleSelectField';
import { useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';

export const OperationsInformation = () => {
    const { selectOptions, productServices: productServicesList } = useVerificationContext();
    const { values, setFieldValue, submitCount, errors } = useFormikContext<FormValues>();
    const { userInfo } = useSelector(selectComponentResources);
    const [isPortlProduct, setIsPortlProduct] = useState<boolean>(false);
    const [bankingExpanded, setBankingExpanded] = useState<boolean>(true);
    const [cryptoExpanded, setCryptoExpanded] = useState<boolean>(true);
    const [portlExpanded, setPortlExpanded] = useState<boolean>(true);
    const [merchantExpanded, setMerchantExpanded] = useState<boolean>(true);
    const companyName = localStorage?.getItem("companyName")
    const showBankingSection = useMemo(
        () => hasBankingProduct(userInfo?.productServices, productServicesList),
        [userInfo?.productServices, productServicesList]
    );

    const showCryptoOTCSection = useMemo(
        () => hasCryptoOTCProduct(userInfo?.productServices, productServicesList),
        [userInfo?.productServices, productServicesList]
    );

    const showPortlSection = useMemo(
        () => hasPortlProduct(userInfo?.productServices, productServicesList),
        [userInfo?.productServices, productServicesList]
    );

    const showMerchantSection = useMemo(
        () => hasMerchantProduct(userInfo?.productServices, productServicesList),
        [userInfo?.productServices, productServicesList]
    );

    const otherFundsSelected = useMemo(
        () =>
            values.operationsInformation.sourceOfFundsIds &&
            values.operationsInformation.sourceOfFundsIds.some(
                (id) =>
                    id ==
                    (selectOptions?.fundingSources?.find((type) => type.label === 'Other')?.value ??
                        0)
            ),
        [selectOptions?.fundingSources, values.operationsInformation.sourceOfFundsIds]
    );
    useEffect(() => {
        if (!otherFundsSelected) setFieldValue(fieldName('sourceOfFundsOther'), null);
    }, [otherFundsSelected, setFieldValue]);

    useEffect(() => {
        setIsPortlProduct(hasPortlProduct(userInfo?.productServices, productServicesList) ?? false);
    }, [userInfo?.productServices, productServicesList]);

    useEffect(() => {
        setFieldValue('operationsInformation.isPortlProduct', isPortlProduct);
    }, [isPortlProduct]);

    const prevSubmitCount = useRef(submitCount);
    useEffect(() => {
        if (submitCount === prevSubmitCount.current) return;
        prevSubmitCount.current = submitCount;

        const ops = (errors as any)?.operationsInformation ?? {};

        const bankingFields = [
            'anticipatedMonthlyTransactionCountId',
            'anticipatedMonthlyVolumeUsdId',
            'countriesReceivingPaymentsFrom',
            'countriesSendingPaymentsTo',
        ];
        const cryptoFields = [
            'blockchainAddresses',
            'industryType',
            'digitalAssetsTrading',
            'anticipatedMonthlyCryptoTransactionsId',
            'anticipatedMonthlyCryptoVolumeUsdId',
            'cryptoBusinessPlan',
        ];
        const portlFields = [
            'cryptoPaymentTypes',
            'integrationMethodId',
            'expectedMonthlyPaymentVolumeUsdId',
            'averageTransactionSize',
            'settlementPreferenceId',
        ];
        const merchantFields = [
            'bAcceptCardPayments',
            'customerTrafficCountries',
            'historicalChargebackRate',
            'historicalRefundRate',
            'productsServicesSold',
        ];

        if (bankingFields.some((f) => ops[f])) setBankingExpanded(true);
        if (cryptoFields.some((f) => ops[f])) setCryptoExpanded(true);
        if (portlFields.some((f) => ops[f])) setPortlExpanded(true);
        if (merchantFields.some((f) => ops[f])) setMerchantExpanded(true);
    }, [submitCount, errors]);

    const integrationMethodOptions: SelectOption[] = [
        { label: 'API Integration', value: 1 },
        { label: 'Hosted Payment Page', value: 2 },
        { label: 'E-commerce Plugin', value: 3 },
        { label: 'Other', value: 4 },
    ];

    const settlementPreferenceOptions: SelectOption[] = [
        { label: 'Keep as Crypto', value: 1 },
        { label: 'Convert to Fiat', value: 2 },
        { label: 'Combination of Both', value: 3 },
    ];
    const mailToDomain = companyName?.toLowerCase()?.replace(/\s+/g, "")

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    alignItems: 'center',
                }}
            >
                <BackButton />
                <a href={`mailto:onboarding@${mailToDomain}.com`} style={{ textDecoration: 'none' }}>
                    <Button priority="secondary" variety="small" type="button">
                        Contact Us
                    </Button>
                </a>
            </div>
            <h2>Entity Operations Information</h2>

            {/* General Business Operations */}
            <FormTextField field={fieldName('webAddress')} label="Website Address *" />
            <div className="FormSideBySide">
                <div className="FormBuilderComponent SideBySide">
                    <FormPhoneNumberField
                        field={fieldName('phoneNumber')}
                        label="Business Phone Number *"
                    />
                </div>
                <FormTextField
                    field={fieldName('supportEmail')}
                    label="Support Email *"
                    sideBySide
                />
            </div>

            <FormTextField
                field={fieldName('purposeOfAccountOther')}
                label="Purpose of Account *"
            />

            <RadioButtons
                options={['Yes', 'No', 'NA']}
                fieldname={fieldName('bPubliclyListed')}
                label="Is business a publicly listed company? If so, list exchange symbol and exchange where traded."
            />
            {values.operationsInformation.bPubliclyListed === 'Yes' && (
                <div className="FormSideBySide">
                    <FormTextField
                        field={fieldName('ticker')}
                        label="Exchange Symbol"
                        sideBySide
                        tooltip="Unique code or ticket symbol your company uses on any digital asset exchange or trading platform (if applicable)."
                    />
                    <FormTextField field={fieldName('exchanges')} label="Exchange(s)" sideBySide />
                </div>
            )}

            <FormMultiSelectField
                fieldName={fieldName('operationRegionIds')}
                options={selectOptions?.regionsOfOperation ?? []}
                label="Regions of Business Operations *"
                tooltip="Countries or regions where your business actively operates or offers services."
            />

            <FormMultiSelectField
                fieldName={fieldName('businessActivityIds')}
                options={selectOptions?.businessActivities ?? []}
                label="Main Business Activity *"
            />

            <FormMultiSelectField
                fieldName={fieldName('sourceOfFundsIds')}
                options={selectOptions?.fundingSources ?? []}
                label="Source of Funds *"
            />
            {otherFundsSelected && (
                <FormTextField
                    field={fieldName('sourceOfFundsOther')}
                    label="Other Source of Funds"
                />
            )}

            {/* Banking & Payments (Fiat) Questions - Collapsible */}
            {showBankingSection && (
                <div className="CollapsibleSection">
                    <div
                        className="CollapsibleHeader"
                        onClick={() => setBankingExpanded(!bankingExpanded)}
                    >
                        <h3>Banking & Payments (Fiat) Questions</h3>
                        <span className={`CollapsibleArrow ${bankingExpanded ? 'expanded' : ''}`}>
                            ▼
                        </span>
                    </div>
                    {bankingExpanded && (
                        <div className="CollapsibleContent">
                            <FormTextField
                                field={fieldName('activeBanks')}
                                label="Primary Bank Name for Incoming/Outgoing Transfers"
                            />
                            <SelectOptionRadioButton
                                options={selectOptions?.transactionVolumes ?? []}
                                fieldname={fieldName('anticipatedMonthlyTransactionCountId')}
                                label="Anticipated Monthly Transaction Count *"
                            />
                            <SelectOptionRadioButton
                                options={selectOptions?.monthlyUsdEquivalents ?? []}
                                fieldname={fieldName('anticipatedMonthlyVolumeUsdId')}
                                label="Anticipated Monthly Volume (USD) *"
                            />
                            <WrappedFormSingleSelectField
                                fieldName={fieldName('countriesSendingPaymentsTo')}
                                label="Countries You Will Be Sending Payments To"
                                options={
                                    selectOptions?.availableCountries?.map((c) => ({
                                        label: c.countryName,
                                        value: c.iso2,
                                    })) ?? []
                                }
                            />
                            <WrappedFormSingleSelectField
                                fieldName={fieldName('countriesReceivingPaymentsFrom')}
                                label="Countries You Will Be Receiving Payments From"
                                options={
                                    selectOptions?.availableCountries?.map((c) => ({
                                        label: c.countryName,
                                        value: c.iso2,
                                    })) ?? []
                                }
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Crypto OTC Questions - Collapsible */}
            {showCryptoOTCSection && (
                <div className="CollapsibleSection">
                    <div
                        className="CollapsibleHeader"
                        onClick={() => setCryptoExpanded(!cryptoExpanded)}
                    >
                        <h3>Crypto OTC Questions</h3>
                        <span className={`CollapsibleArrow ${cryptoExpanded ? 'expanded' : ''}`}>
                            ▼
                        </span>
                    </div>
                    {cryptoExpanded && (
                        <div className="CollapsibleContent">
                            <FormTextField
                                field={fieldName('blockchainAddresses')}
                                label="Blockchain Address(es) for Transactions"
                                placeholder="Enter blockchain addresses"
                            />
                            <FormTextField
                                field={fieldName('industryType')}
                                label="Industry Type"
                                placeholder="e.g., DeFi, NFT, Exchange, etc."
                            />
                            <FormTextField
                                field={fieldName('digitalAssetsTrading')}
                                label="Digital Assets You Will Be Trading"
                                placeholder="e.g., BTC, ETH, USDC, etc."
                            />
                            <div className="FormSideBySide">
                                <WrappedFormSingleSelectField
                                    fieldName={fieldName('anticipatedMonthlyCryptoTransactionsId')}
                                    options={
                                        selectOptions?.transactionVolumes ?? ([] as SelectOption[])
                                    }
                                    label="Anticipated Monthly Crypto Transactions"
                                    sideBySide
                                />
                                <WrappedFormSingleSelectField
                                    fieldName={fieldName('anticipatedMonthlyCryptoVolumeUsdId')}
                                    options={
                                        selectOptions?.monthlyUsdEquivalents ??
                                        ([] as SelectOption[])
                                    }
                                    label="Anticipated Monthly Crypto Volume (USD)"
                                    sideBySide
                                />
                            </div>
                            <FormTextField
                                type="textarea"
                                field={fieldName('cryptoBusinessPlan')}
                                label="Business Plan (Optional)"
                                placeholder="Describe your crypto business plan"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* PORTL Questions (Crypto Acquiring) - Collapsible */}
            {showPortlSection && (
                <div className="CollapsibleSection">
                    <div
                        className="CollapsibleHeader"
                        onClick={() => setPortlExpanded(!portlExpanded)}
                    >
                        <h3>PORTL Questions (Crypto Acquiring)</h3>
                        <span className={`CollapsibleArrow ${portlExpanded ? 'expanded' : ''}`}>
                            ▼
                        </span>
                    </div>
                    {portlExpanded && (
                        <div className="CollapsibleContent">
                            <FormTextField
                                field={fieldName('cryptoPaymentTypes')}
                                label="Types of Crypto Payments You Will Accept"
                                placeholder="e.g., BTC, ETH, stablecoins, etc."
                            />
                            <WrappedFormSingleSelectField
                                fieldName={fieldName('integrationMethodId')}
                                options={integrationMethodOptions}
                                label="Integration Method"
                            />
                            <div className="FormSideBySide">
                                <WrappedFormSingleSelectField
                                    fieldName={fieldName('expectedMonthlyPaymentVolumeUsdId')}
                                    options={
                                        selectOptions?.monthlyUsdEquivalents ??
                                        ([] as SelectOption[])
                                    }
                                    label="Expected Monthly Payment Volume (USD)"
                                    sideBySide
                                />
                                <FormTextField
                                    field={fieldName('averageTransactionSize')}
                                    label="Average Transaction Size"
                                    placeholder="e.g., $100"
                                    sideBySide
                                    type='number'
                                />
                            </div>
                            <WrappedFormSingleSelectField
                                fieldName={fieldName('settlementPreferenceId')}
                                options={settlementPreferenceOptions}
                                label="Settlement Preference"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Merchant Services Questions - Collapsible */}
            {showMerchantSection && (
                <div className="CollapsibleSection">
                    <div
                        className="CollapsibleHeader"
                        onClick={() => setMerchantExpanded(!merchantExpanded)}
                    >
                        <h3>Merchant Services Questions</h3>
                        <span className={`CollapsibleArrow ${merchantExpanded ? 'expanded' : ''}`}>
                            ▼
                        </span>
                    </div>
                    {merchantExpanded && (
                        <div className="CollapsibleContent">
                            <RadioButtons
                                options={['Yes', 'No']}
                                fieldname={fieldName('bAcceptCardPayments')}
                                label="Do you currently accept card payments?"
                            />
                            <FormMultiSelectField
                                fieldName={fieldName('customerTrafficCountries')}
                                label="Countries Where You Have Customer Traffic"
                                options={
                                    selectOptions?.availableCountries?.map((c) => ({
                                        label: c.countryName,
                                        value: c.isoCode,
                                    })) ?? []
                                }
                            />
                            <div className="FormSideBySide">
                                <FormTextField
                                    field={fieldName('historicalChargebackRate')}
                                    label="Historical Chargeback Rate (%)"
                                    placeholder="e.g., 0.5%"
                                    sideBySide
                                    type='number'
                                />
                                <FormTextField
                                    field={fieldName('historicalRefundRate')}
                                    label="Historical Refund Rate (%)"
                                    placeholder="e.g., 2%"
                                    sideBySide
                                    type='number'
                                />
                            </div>
                            <FormTextField
                                type="textarea"
                                field={fieldName('productsServicesSold')}
                                label="Type of Products/Services Sold"
                                placeholder="Describe what you sell"
                            />
                        </div>
                    )}
                </div>
            )}

        </>
    );
};

const fieldName = (name: string) => `operationsInformation.${name}`;
